import { BodyMedium, SmallBodyRegular } from 'components/Text';
import { useCurrentCountry, useEndDate } from 'utils/hooks';
import { useEffect, useState } from 'react';
import DimensionIcon from 'components/DimensionIcon';
import DownloadButton from 'components/Button/DownloadButton';
import DrawerSectionHeading from 'components/DrawerSectionHeading';
import Expander from 'components/Expander';
import { FiPlus } from 'react-icons/fi';
import Link from 'components/Link';
import { PlaceholderText } from 'components/Placeholders';
import { SoftButton } from 'components/Button';
import Stack from 'components/Stack';
import VertLine from 'components/VertLine';
import { buildExportFilename, downloadItemsAsCSV } from 'utils/formatCSV';
import { getPathFromNode } from 'utils/helpers';
import styled from 'styled-components';
import useFlavorCategories from './useFlavorCategories';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';

const sourceByDataSource = {
    google: 'google',
    tiktok: 'tiktok',
    pop_index: 'pop-index',
};

const CategoryRowWrapper = styled.div`
    display: grid;
    grid-template-columns: min-content 1fr;
    align-items: center;
    gap: 0.125rem 0.375rem;
`;

const NameMetricWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    color: ${({ theme }) => theme.gray_900};
`;

const CategoryName = styled(Link)`
    ${BodyMedium.Fragment}
    text-transform: capitalize;
    color: inherit;
    &:hover,
    &:focus-visible {
        color: ${({ theme }) => theme.purple_1};
    }
`;

const CategoryIconWrapper = styled(SmallBodyRegular)`
    color: ${({ theme }) => theme.gray_600};
    display: flex;
`;

const MetricWrapper = styled(BodyMedium)`
    text-transform: uppercase;
    tabular-nums: tabular-nums;
`;

const ParentName = styled(SmallBodyRegular)`
    color: ${({ theme }) => theme.gray_600};
    text-transform: capitalize;
`;

function PlaceholderCategoryRow() {
    return (
        <CategoryRowWrapper>
            <CategoryIconWrapper>
                <DimensionIcon dimensionType="category" />
            </CategoryIconWrapper>
            <SmallBodyRegular
                css={`
                    color: ${({ theme }) => theme.gray_600};
                `}
            >
                <PlaceholderText width="8ch" />
            </SmallBodyRegular>
            <span />
            <NameMetricWrapper>
                <div css="flex-grow: 1;">
                    <BodyMedium>
                        <PlaceholderText width="14ch" />
                    </BodyMedium>
                </div>
                <MetricWrapper>
                    <PlaceholderText width="4ch" />
                </MetricWrapper>
            </NameMetricWrapper>
        </CategoryRowWrapper>
    );
}

function CategoryRow({ name, parentName, metricText, linkTo, as }) {
    return (
        <CategoryRowWrapper as={as}>
            <CategoryIconWrapper>
                <DimensionIcon dimensionType="category" />
            </CategoryIconWrapper>
            <ParentName>{parentName || 'Category'}</ParentName>
            <span />
            <NameMetricWrapper>
                <div css="flex-grow: 1;">
                    <CategoryName to={linkTo}>{name}</CategoryName>
                </div>
                <MetricWrapper>{metricText}</MetricWrapper>
            </NameMetricWrapper>
        </CategoryRowWrapper>
    );
}

function FlavorCategoriesDrawer({
    country,
    category,
    dataSource,
    flavor,
    flavorName,
    metric,
    displayQty = 6,
}) {
    const getHierarchy = useGetHierarchy();
    const source = sourceByDataSource[dataSource] || dataSource;
    const countryObj = useCurrentCountry();
    const date = useEndDate(source);
    const orderBy = metric.getSortOption({ ascending: false }).sortBy;
    const categoriesQuery = useFlavorCategories({
        country: country ? [country] : null,
        category: category ? [category] : null,
        dataSource,
        flavor: flavor ? [flavor] : null,
        order_by: orderBy,
        ascending: false,
        enabled: Boolean(country && category && dataSource && flavor),
    });
    const categories = categoriesQuery.data?.data;
    const [numToDisplay, setNumToDisplay] = useState(displayQty);
    useEffect(() => {
        if (displayQty > numToDisplay) setNumToDisplay(displayQty);
    }, [displayQty]);
    return (
        <Stack $space="1rem">
            <DrawerSectionHeading>
                Categories
                <DownloadButton
                    disabled={
                        categoriesQuery.isLoading ||
                        categoriesQuery.isError ||
                        !categories?.length
                    }
                    fetching={categoriesQuery.isLoading}
                    onClick={() => {
                        downloadItemsAsCSV(
                            buildExportFilename({
                                date,
                                country: countryObj,
                                source,
                                entity: flavorName,
                                type: 'related-category',
                                kind: 'data',
                            }),
                            categories.map(({ name, data }) => ({
                                name,
                                volume: data?.[orderBy],
                            })),
                            [
                                { key: 'name', label: 'category' },
                                {
                                    key: 'volume',
                                    label: metric.tableHeadColumnLabel,
                                },
                            ],
                        );
                    }}
                    mixpanelMeta={{ contentKey: 'FlavorCategories' }}
                    css="margin-left: auto;"
                />
            </DrawerSectionHeading>
            <Expander>
                {categoriesQuery.isError && (
                    <SmallBodyRegular
                        css={`
                            text-align: center;
                            display: block;
                        `}
                    >
                        Error loading categories
                    </SmallBodyRegular>
                )}
                <Stack
                    as="ol"
                    $space=".875rem"
                    $direction="column"
                    css={`
                        margin: 0;
                        list-style: none;
                        padding: 0;
                    `}
                >
                    {categoriesQuery.isLoading &&
                        new Array(displayQty)
                            .fill('')
                            .map((_s, idx) => (
                                <PlaceholderCategoryRow
                                    key={`placeholder-${idx}`}
                                />
                            ))}
                    {categoriesQuery.isSuccess
                        ? categories
                              ?.slice(0, numToDisplay)
                              .map(
                                  ({ flavor: id, name, data, hierarchies }) => {
                                      const hierarchy =
                                          getHierarchy(hierarchies);
                                      return (
                                          <CategoryRow
                                              as="li"
                                              key={id}
                                              name={name}
                                              parentName={
                                                  hierarchy?.hierarchy_hint
                                                      ?.cat4
                                              }
                                              metricText={metric.format(
                                                  data?.[orderBy],
                                              )}
                                              linkTo={getPathFromNode(
                                                  {
                                                      name,
                                                      id,
                                                      depth: 'cat5',
                                                      type: 'category',
                                                  },
                                                  hierarchy?.parent,
                                                  dataSource !== 'pop_index'
                                                      ? dataSource
                                                      : null,
                                              )}
                                          />
                                      );
                                  },
                              )
                        : null}
                </Stack>
            </Expander>
            <Stack
                $space=".75rem"
                $direction="row"
                css="display: flex; align-items: center;"
            >
                {categories?.length > displayQty ? (
                    <SoftButton
                        IconLeft={FiPlus}
                        size="sm"
                        onClick={() =>
                            setNumToDisplay(numToDisplay + displayQty)
                        }
                        isDisabled={numToDisplay >= categories?.length}
                    >
                        Show more
                    </SoftButton>
                ) : null}
                {numToDisplay > displayQty ? (
                    <>
                        <VertLine />
                        <SoftButton
                            size="sm"
                            onClick={() => setNumToDisplay(displayQty)}
                        >
                            Hide All
                        </SoftButton>
                    </>
                ) : null}
            </Stack>
        </Stack>
    );
}

export default FlavorCategoriesDrawer;
